import { BaseNumber, CheckedAddress, NormalizedNumber, UnixDuration, UnixTimestamp } from '@sparkdotfi/common-universal'
import { QueryKey, queryOptions, useSuspenseQuery } from '@tanstack/react-query'
import { range } from 'remeda'
import { Address, parseAbi } from 'viem'
import { Config } from 'wagmi'
import { readContract } from 'wagmi/actions'
import { z } from 'zod'

import { spark2ApiUrl } from '@/config/consts'
import { spkStakingModuleAbi, spkStakingModuleAddress } from '@/config/contracts-generated'
import { normalizedNumberSchema } from '@/domain/common/validation'
import { getContractAddress } from '@/domain/hooks/useContractAddress'
import { useSandbox } from '@/domain/sandbox'
import { TokenRepository } from '@/domain/token-repository/TokenRepository'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { season3Active } from '@/features/points/consts'
import { SuspenseQueryWith } from '@/utils/types'

export interface SpkStakingDataParams {
  chainId: number
  account: CheckedAddress | undefined
  wagmiConfig: Config
  tokenRepository: TokenRepository
  isInSandbox: boolean
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function spkStakingDataQueryOptions({
  chainId,
  account,
  wagmiConfig,
  tokenRepository,
  isInSandbox,
}: SpkStakingDataParams) {
  return queryOptions({
    queryKey: spkStakingDataQueryKey({ account, chainId }),
    queryFn: () => spkStakingDataQueryFunction({ chainId, account, wagmiConfig, tokenRepository }),
    refetchInterval: (query) => {
      if (query.state.data?.isOutOfSync) {
        return 2_000
      }

      if (isInSandbox) {
        return 5_000
      }

      return undefined
    },
  })
}

export interface SpkStakingDataQueryKeyParams {
  account: Address | undefined
  chainId: number
}

export function spkStakingDataQueryKey({ account, chainId }: SpkStakingDataQueryKeyParams): QueryKey {
  return ['spk-staking', 'spkStakingData', chainId, account]
}

export type SpkStakingDataQueryFunctionParams = Omit<SpkStakingDataParams, 'isInSandbox'>

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export async function spkStakingDataQueryFunction({
  chainId,
  account,
  wagmiConfig,
  tokenRepository,
}: SpkStakingDataQueryFunctionParams) {
  const findToken = tokenRepository.findOneBySymbol
  const spk = findToken(TokenSymbol('SPK'))

  async function fetchOnchainAccountData(): Promise<OnchainAccountData> {
    if (!account) {
      return Promise.resolve({
        amountStaked: NormalizedNumber.ZERO,
        slashableBalance: NormalizedNumber.ZERO,
      })
    }
    const [staked, slashableBalance] = await Promise.all([
      readContract(wagmiConfig, {
        address: getContractAddress(spkStakingModuleAddress, chainId),
        abi: spkStakingModuleAbi,
        functionName: 'activeSharesOf',
        args: [account],
        chainId,
      }),
      readContract(wagmiConfig, {
        address: getContractAddress(spkStakingModuleAddress, chainId),
        abi: spkStakingModuleAbi,
        functionName: 'slashableBalanceOf',
        args: [account],
        chainId,
      }),
    ])

    return {
      amountStaked: BaseNumber(staked).toNormalizedUnit(spk.decimals),
      slashableBalance: BaseNumber(slashableBalance).toNormalizedUnit(spk.decimals),
    }
  }

  async function fetchBaDataSeason(season: 1 | 2 | 3): Promise<z.infer<typeof baDataResponseSchema>> {
    if (!account) {
      return {
        slashable_balance_normalized: NormalizedNumber.ZERO,
        points_normalized: NormalizedNumber.ZERO,
        points_per_second_normalized: NormalizedNumber.ZERO,
        timestamp_updated: 0,
      }
    }

    const seasonQuery = season !== 1 ? `?season=${season}` : ''
    const res = await fetch(`${spark2ApiUrl}/spk-staking/${account}/${seasonQuery}`)
    if (!res.ok) {
      throw new Error('Failed to fetch rewards')
    }

    return baDataResponseSchema.parse(await res.json())
  }

  async function fetchVaultData(): Promise<VaultData> {
    const [currentEpoch, _epochDuration, epochDurationInit] = await Promise.all([
      readContract(wagmiConfig, {
        address: getContractAddress(spkStakingModuleAddress, chainId),
        abi: spkStakingModuleAbi,
        functionName: 'currentEpoch',
        chainId,
      }),
      readContract(wagmiConfig, {
        address: getContractAddress(spkStakingModuleAddress, chainId),
        abi: spkStakingModuleAbi,
        functionName: 'epochDuration',
        chainId,
      }),
      readContract(wagmiConfig, {
        address: getContractAddress(spkStakingModuleAddress, chainId),
        abi: spkStakingModuleAbi,
        functionName: 'epochDurationInit',
        chainId,
      }),
    ])

    const nextEpochEnd = UnixTimestamp({ seconds: Number(currentEpoch + 2n) * _epochDuration + epochDurationInit })
    const epochDuration = UnixDuration({ seconds: _epochDuration })

    if (!account) {
      return {
        nextEpochEnd,
        epochDuration,
        withdrawals: [],
      }
    }

    const withdrawals = await Promise.all(
      range(Math.max(0, Number(currentEpoch) - 25), Number(currentEpoch) + 1).map(async (i) => {
        const epoch = BigInt(i) + 1n

        const [amount, isWithdrawalsClaimed] = await Promise.all([
          readContract(wagmiConfig, {
            address: getContractAddress(spkStakingModuleAddress, chainId),
            abi: spkStakingModuleAbi,
            functionName: 'withdrawalSharesOf',
            args: [epoch, account],
            chainId,
          }),
          readContract(wagmiConfig, {
            address: getContractAddress(spkStakingModuleAddress, chainId),
            abi: spkStakingModuleAbi,
            functionName: 'isWithdrawalsClaimed',
            args: [epoch, account],
            chainId,
          }),
        ])

        return {
          epoch,
          amount: BaseNumber(amount).toNormalizedUnit(spk.decimals),
          isWithdrawalsClaimed,
        }
      }),
    )

    const pendingWithdrawals = withdrawals.filter((w) => w.epoch >= currentEpoch)

    const formattedPendingWithdrawals = pendingWithdrawals.map((w) => ({
      epochs: [w.epoch],
      amount: w.amount,
      claimableAt: UnixTimestamp({ seconds: Number(w.epoch + 1n) * _epochDuration + epochDurationInit }).toDate(),
    }))

    const aggregatedPreviousWithdrawals = {
      ...withdrawals
        .filter((w) => w.epoch < currentEpoch)
        .filter((w) => w.amount.gt(0))
        .filter((w) => !w.isWithdrawalsClaimed)
        .reduce(
          (acc, curr) => ({
            epochs: [...acc.epochs, curr.epoch],
            amount: acc.amount.plus(curr.amount),
          }),
          { epochs: [] as bigint[], amount: NormalizedNumber.ZERO },
        ),
      claimableAt: UnixTimestamp({ seconds: Number(currentEpoch) * _epochDuration + epochDurationInit }).toDate(),
    }

    return {
      nextEpochEnd,
      epochDuration,
      withdrawals: [...formattedPendingWithdrawals, aggregatedPreviousWithdrawals].filter((w) => !w.amount.isZero()),
    }
  }

  async function fetchTimestamp(): Promise<UnixTimestamp> {
    const timestamp = await readContract(wagmiConfig, {
      address: '0xcA11bde05977b3631167028862bE2a173976CA11',
      abi: parseAbi(['function getCurrentBlockTimestamp() public view returns (uint256 timestamp)']),
      functionName: 'getCurrentBlockTimestamp',
      chainId,
    })

    return UnixTimestamp({ seconds: timestamp })
  }

  const currentSeason = season3Active ? 3 : 2
  const previousSeason = currentSeason === 3 ? 2 : 1

  const [
    { amountStaked, slashableBalance },
    currentSeasonBaData,
    previousSeasonBaData,
    { withdrawals, nextEpochEnd, epochDuration },
    timestamp,
  ] = await Promise.all([
    fetchOnchainAccountData(),
    fetchBaDataSeason(currentSeason),
    fetchBaDataSeason(previousSeason),
    fetchVaultData(),
    fetchTimestamp(),
  ])

  return {
    amountStaked,
    slashableBalance,
    pointsLastUpdatedAmount: currentSeasonBaData.points_normalized,
    pointsPerSecond: currentSeasonBaData.points_per_second_normalized,
    pointsLastUpdatedTimestamp: currentSeasonBaData.timestamp_updated,
    withdrawals,
    timestamp,
    nextEpochEnd,
    epochDuration,
    previousSeasonPoints: previousSeasonBaData.points_normalized,
    isOutOfSync: !slashableBalance.eq(currentSeasonBaData.slashable_balance_normalized),
  }
}

const baDataResponseSchema = z.object({
  slashable_balance_normalized: normalizedNumberSchema,
  points_normalized: normalizedNumberSchema,
  points_per_second_normalized: normalizedNumberSchema,
  timestamp_updated: z.number(),
})

export interface Withdrawal {
  epochs: bigint[]
  amount: NormalizedNumber
  claimableAt: Date
}

export interface OnchainAccountData {
  amountStaked: NormalizedNumber
  slashableBalance: NormalizedNumber
}

export interface VaultData {
  nextEpochEnd: UnixTimestamp
  epochDuration: UnixDuration
  withdrawals: Withdrawal[]
}

export interface SpkStakingData {
  amountStaked: NormalizedNumber
  slashableBalance: NormalizedNumber
  pointsLastUpdatedAmount: NormalizedNumber
  pointsPerSecond: NormalizedNumber
  pointsLastUpdatedTimestamp: number
  withdrawals: Withdrawal[]
  timestamp: UnixTimestamp
  nextEpochEnd: UnixTimestamp
  epochDuration: UnixDuration
  previousSeasonPoints: NormalizedNumber
  isOutOfSync: boolean
}

export interface UseSpkStakingDataParams {
  chainId: number
  account: CheckedAddress | undefined
  wagmiConfig: Config
  tokenRepository: TokenRepository
}

export type UseSpkStakingDataResult = SuspenseQueryWith<{
  spkStakingData: SpkStakingData
}>

export function useSpkStakingData({
  chainId,
  account,
  wagmiConfig,
  tokenRepository,
}: UseSpkStakingDataParams): UseSpkStakingDataResult {
  const { isInSandbox } = useSandbox()
  const res = useSuspenseQuery(
    spkStakingDataQueryOptions({ chainId, account, wagmiConfig, tokenRepository, isInSandbox }),
  )

  return {
    ...res,
    spkStakingData: res.data,
  }
}
